import { ReactNode, useCallback, useState } from "react";

import { CrmEntityType, CrmProvider } from "@/pages/Private/redux/crm/crm.schema";

import { AddToCrmListOption } from "./addToCrmListOptions";
import { AddToCrmModal, AddToCrmOwner } from "./AddToCrmModal";

interface UseAddToCrmModalArgs {
	/** How many prospects the submit acts on — shown in the modal's description line. */
	selectedCount: number;
	/** The submit mutation's in-flight state, forwarded to the modal's save button. */
	isLoading?: boolean;
	/**
	 * The list membership this surface is showing. Passing it turns the modal's list picker
	 * on (Dynamics with a marketing feature enabled) and preselects this list.
	 */
	currentList?: AddToCrmListOption | null;
	/**
	 * Commits the picked entities/owner. Must handle its own errors (toast) rather than throw —
	 * the modal closes as soon as the promise settles.
	 */
	onSubmit: (
		provider: CrmProvider,
		entities: CrmEntityType[],
		owner?: AddToCrmOwner,
		list?: AddToCrmListOption
	) => Promise<void>;
}

export interface AddToCrmModalHost {
	openAddToCrm: (provider: CrmProvider) => void;
	renderAddToCrmModal: () => ReactNode;
}

/**
 * Hosts the shared {@link AddToCrmModal} for surfaces that act on a single provider pick (the
 * lead-panel / Inbox "Add to" menu): `openAddToCrm(provider)` opens the picker, submit runs the
 * host's `onSubmit` and closes. Render-from-hook idiom, cf. `useWebhooks.renderModal`.
 */
export const useAddToCrmModal = ({
	selectedCount,
	isLoading,
	currentList,
	onSubmit,
}: UseAddToCrmModalArgs): AddToCrmModalHost => {
	const [provider, setProvider] = useState<CrmProvider | null>(null);

	const openAddToCrm = useCallback((next: CrmProvider) => setProvider(next), []);

	const submit = async (
		pickedProvider: CrmProvider,
		entities: CrmEntityType[],
		owner?: AddToCrmOwner,
		list?: AddToCrmListOption
	) => {
		await onSubmit(pickedProvider, entities, owner, list);
		setProvider(null);
	};

	const renderAddToCrmModal = () =>
		provider ? (
			<AddToCrmModal
				isLoading={isLoading}
				isOpen={true}
				currentList={currentList}
				provider={provider}
				selectedCount={selectedCount}
				onClose={() => setProvider(null)}
				onSubmit={(pickedProvider, entities, owner, list) =>
					void submit(pickedProvider, entities, owner, list)
				}
			/>
		) : null;

	return { openAddToCrm, renderAddToCrmModal };
};
